/**
 * Constantes FR du Test de qualification — libellés jours/créneaux, motifs
 * de correction, intentions de réorientation. Regroupées ici pour rester
 * "faciles à changer" (cf. mission : « libellés des 3 créneaux dans une
 * constante SLOT_LABELS facile à changer »).
 */
import type { Gender, ConnVerdict, CorrectionMotif, Criteria, DayKey, DeviceClass, ReorientIntent, SlotKey, StartAvailability, WorkSituation } from "./types";
import { CORRECTION_REASONS } from "@/regie-test/correctionReasons";
import { GRILLE_DES_SHIFTS } from "@/shared/grilleDesShifts";
import { minutesToHHMM } from "@/shared/shiftTime";

// ─── Jours ──────────────────────────────────────────────────────────────────

export const DAY_LABELS: Record<DayKey, string> = {
  mon: "Lun",
  tue: "Mar",
  wed: "Mer",
  thu: "Jeu",
  fri: "Ven",
  sat: "Sam",
  sun: "Dim",
};

export const DAY_LABELS_FULL: Record<DayKey, string> = {
  mon: "Lundi",
  tue: "Mardi",
  wed: "Mercredi",
  thu: "Jeudi",
  fri: "Vendredi",
  sat: "Samedi",
  sun: "Dimanche",
};

// ─── Shifts — les VRAIS shifts de La Régie. Kayla, 12-09-2026 : QUATRE
// créneaux de 6 h, à l'heure PLEINE, ancrés sur 03:00, en heure de PARIS.
//
// Depuis le 29-10-2026 ce n'est plus une copie : `SHIFTS` DÉRIVE de
// `src/shared/grilleDesShifts.ts`, la seule définition du dépôt (avec
// `panels/organisation/shiftsFixes.ts`, qui en dérive aussi). Les surfaces
// hors dépôt restent : le back (`app/manager/grille.py` côté Cyrano, les
// lettres que le POST /profile accepte — une lettre ajoutée ici sans y être
// ajoutée là-bas fait rendre 422 à l'envoi du profil), les noms des salons
// vocaux Discord (« (03h00–09h00) »), d'où Convex DÉRIVE le rôle de chaque
// créneau et la grille que Francis dit, et le `SHIFT_MIN` du bot Cerbère.
//
// 🪤 Les lettres e et f ont EXISTÉ (18-08 → 12-09). Des candidats les ont
// cochées ; leur fiche porte la grille qu'ils ont VUE (`ShiftVu`), c'est elle
// qui donne leur sens. Ici on ne les relit pas : une lettre inconnue est
// ignorée, jamais réinterprétée dans la grille du jour. ──────────────────────

/** Fuseau d'ANCRAGE des heures ci-dessous (celles que Kayla configure dans
 *  Organisation → Shifts). Toute conversion vers le fuseau d'un candidat
 *  part de cette base — cf. `ShiftAvailabilityPicker`.
 *
 *  Ré-export, plus une seconde constante : c'est le MÊME fuseau que celui des
 *  shifts du cockpit, et deux copies d'un fuseau, c'est celle qu'on lit qui
 *  ment (cf. `FUSEAU_DES_SHIFTS`). */
export { FUSEAU_DES_SHIFTS as SHIFT_BASE_TIMEZONE } from "@/shared/shiftTime";

export interface ShiftDef {
  key: SlotKey;
  /** Lettre du shift (identifiant produit — cf. maquette « Matin (shift A) »). */
  letter: string;
  name: string;
  /** Minutes depuis minuit, heure de `SHIFT_BASE_TIMEZONE`. */
  startMin: number;
  endMin: number;
  /** `false` : le créneau existe dans le planning mais la candidature ne le
   *  PROPOSE pas. Kayla, 13-09-2026 : « le shift de 3 h à 9 h, il m'intéresse
   *  pas ». La grille, elle, reste à 24 h sans trou (et `embauche.test.ts` y
   *  tient) — c'est le recrutement qui ne le vise pas, pas la journée qui
   *  perd six heures. Absent = recruté.
   *  🩸 07-10-2026 : le 03–09 est ROUVERT au recrutement (Kayla, devant le
   *  formulaire : « pourquoi n'y a-t-il pas le Shift A ? »). Aucun créneau ne
   *  porte le drapeau aujourd'hui ; il reste pour la prochaine fois. */
  recrute?: false;
}

export const SHIFTS: readonly ShiftDef[] = GRILLE_DES_SHIFTS.map((c) => ({
  key: c.key,
  letter: c.lettre,
  name: c.nom,
  startMin: c.startMinute,
  endMin: c.endMinute,
}));

/** Ce que le formulaire de candidature propose : la grille, moins les créneaux
 *  qu'on ne recrute pas. Les fiches et la couverture, elles, lisent `SHIFTS`
 *  entier — un candidat qui a coché « a » avant le 13-09 garde son sens. */
export const SHIFTS_RECRUTES: readonly ShiftDef[] = SHIFTS.filter(
  (s) => s.recrute !== false,
);

/** Deux grilles disent-elles la MÊME chose ? Compare les créneaux et leurs
 *  bornes, pas l'ordre ni les libellés — renommer « Soir » en « Soirée » ne
 *  change pas les heures qu'un candidat s'est engagé à tenir. */
export function memeGrille(
  a: readonly { key: string; startMin: number; endMin: number }[],
  b: readonly { key: string; startMin: number; endMin: number }[],
): boolean {
  if (a.length !== b.length) return false;
  const bornes = (g: typeof a) =>
    new Map(g.map((s) => [s.key, `${s.startMin}-${s.endMin}`]));
  const ba = bornes(a);
  const bb = bornes(b);
  return [...ba].every(([k, v]) => bb.get(k) === v);
}

/** Compat : `format.ts`, `CoverageTab.tsx` et `CandidateFiche.tsx` lisaient
 *  un `SLOT_LABELS` plat {label,hours} — dérivé de `SHIFTS` (heures de BASE,
 *  Paris) pour ne pas dupliquer. Le picker candidat, lui, affiche ses
 *  propres heures LOCALISÉES dans le fuseau du candidat (cf.
 *  `ShiftAvailabilityPicker`), pas ces heures de base. */
export const SLOT_LABELS: Record<SlotKey, { label: string; hours: string }> =
  Object.fromEntries(
    SHIFTS.map((s) => [
      s.key,
      { label: s.name, hours: `${minutesToHHMM(s.startMin)}–${minutesToHHMM(s.endMin)}` },
    ]),
  ) as Record<SlotKey, { label: string; hours: string }>;

// ─── Motifs de correction — réutilise le référentiel PROD (mêmes slugs,
// mêmes libellés/emoji que La Régie réelle) + l'option "Autre" absente de
// `CORRECTION_REASONS` (gérée à part côté prod). Module pur, zéro
// dépendance réseau — safe à importer (cf. docstring RegieActionBar :
// seuls les COMPOSANTS réseau RegieActionBar/RegiePanel sont hors-limites). ──

export interface MotifOption {
  slug: CorrectionMotif;
  emoji: string;
  label: string;
}

export const CORRECTION_MOTIFS: readonly MotifOption[] = [
  ...CORRECTION_REASONS.map((r) => ({
    slug: r.slug as CorrectionMotif,
    emoji: r.emoji,
    label: r.defaultLabel,
  })),
  { slug: "other", emoji: "✏️", label: "Autre" },
] as const;

// ─── Intentions de réorientation ────────────────────────────────────────────

export interface ReorientOption {
  slug: ReorientIntent;
  emoji: string;
  label: string;
}

export const REORIENT_INTENTS: readonly ReorientOption[] = [
  { slug: "continue", emoji: "💬", label: "Continuer la conversation" },
  { slug: "heat", emoji: "🔥", label: "Faire monter la chaleur" },
  { slug: "sell", emoji: "💸", label: "Tenter une vente" },
] as const;

// ─── Seuil « diplômé » (proposition par défaut du plan, à calibrer par
// Kayla sur les 4 premiers) — pilote la pastille Recommandée/À revoir/
// Écartée de l'admin. ────────────────────────────────────────────────────

export const GRADUATE_THRESHOLD = 0.8;
export const REVIEW_THRESHOLD = 0.6;

// ─── Seuil de session gradué (0..100, CONTRACT §1d) — distinct du legacy
// GRADUATE_THRESHOLD (fraction 0..1 sur fault_catch_rate) : c'est la valeur
// par défaut de `EvalConfig.threshold`, éditable par Kayla (GET/PUT /config,
// jamais en dur ailleurs que cette valeur de repli). ──────────────────────

export const DEFAULT_EVAL_THRESHOLD = 80;

// ─── Libellés vérif technique (appareil / connexion) ───────────────────────

export const DEVICE_CLASS_LABELS: Record<DeviceClass, string> = {
  computer: "Ordinateur",
  mobile: "Mobile",
  tablet: "Tablette",
};

export const CONN_VERDICT_LABELS: Record<ConnVerdict, string> = {
  good: "Bonne",
  ok: "Correcte",
  bad: "Instable",
};

export const CONN_VERDICT_EMOJI: Record<ConnVerdict, string> = {
  good: "✅",
  ok: "🟡",
  bad: "🔴",
};

/** Icône FR des critères de recrutement (bloc « Sa candidature », fiche
 *  admin). Le 🎯 du score est parti avec le test de cartes de La Régie. */
export const CRITERION_ICONS: Record<keyof Criteria, string> = {
  computer: "💻",
  connection: "📶",
  availability: "📅",
};

// ─── Profil : « quand peux-tu commencer » + « ta situation » (choix simples,
// remplacent heures/semaine + date de début — Kayla fixe les heures). ───────

export interface ChoiceOption<T extends string> {
  slug: T;
  emoji: string;
  label: string;
}

export const GENDER_OPTIONS: readonly ChoiceOption<Gender>[] = [
  { slug: "homme", emoji: "👨", label: "Homme" },
  { slug: "femme", emoji: "👩", label: "Femme" },
] as const;

/** L'âge PLANCHER — le poste vend du contenu adulte, c'est un plancher légal,
 *  pas une préférence. Le serveur le revérifie : la route du profil est
 *  publique, et ce qui n'est vérifié qu'ici n'est pas vérifié. */
export const AGE_MINIMUM = 18;

export const START_AVAILABILITY_OPTIONS: readonly ChoiceOption<StartAvailability>[] = [
  { slug: "now", emoji: "⚡", label: "Tout de suite" },
  { slug: "this_week", emoji: "📆", label: "Cette semaine" },
  { slug: "later", emoji: "🗓️", label: "Plus tard" },
] as const;

export const START_AVAILABILITY_LABELS: Record<StartAvailability, string> = {
  now: "Tout de suite",
  this_week: "Cette semaine",
  later: "Plus tard",
};

export const WORK_SITUATION_OPTIONS: readonly ChoiceOption<WorkSituation>[] = [
  { slug: "free", emoji: "🟢", label: "Je suis libre" },
  { slug: "student", emoji: "🎓", label: "Étudiant·e" },
  { slug: "employed", emoji: "💼", label: "J'ai déjà un travail" },
] as const;

export const WORK_SITUATION_LABELS: Record<WorkSituation, string> = {
  free: "Sans emploi (dispo)",
  student: "Étudiant·e",
  employed: "En emploi",
};
